// src/data/dataLoader.js
import { getYearConfig, isMomentoAvailable } from "./dataConfig";

/**
 * Helpers
 */
const safeLoad = async (loader) => {
  try {
    const mod = await loader();
    return mod.default;
  } catch (err) {
    console.warn("Data file not found or failed to load:", err);
    return null;
  }
};

/**
 * Loader map
 * IMPORTANTE:
 * - Todos los paths son estáticos (Webpack-friendly)
 * - Escalable agregando entradas, no lógica
 */
const DATA_LOADERS = {
  "2024-2025": {
    1: {
      docenteData: () =>
        import("./archive/2024-2025/momento1/docenteData.json"),
      interaccionData: () =>
        import("./archive/2024-2025/momento1/interaccionData.json"),
      visitas: () =>
        import("./archive/2024-2025/momento1/visitas.json"),
      progresoData: () =>
        import("./archive/2024-2025/momento1/promedios.json"),
      questions: () =>
        import("./archive/2024-2025/momento1/questions.json"),
      answers: () =>
        import("./archive/2024-2025/momento1/answers.json"),
    },
    2: {
      docenteData: () =>
        import("./archive/2024-2025/momento2/docenteData.json"),
      interaccionData: () =>
        import("./archive/2024-2025/momento2/interaccionData.json"),
      visitas: () =>
        import("./archive/2024-2025/momento2/visitas.json"),
      progresoData: () =>
        import("./archive/2024-2025/momento2/promedios.json"),
      questions: () =>
        import("./archive/2024-2025/momento2/questions.json"),
      answers: () =>
        import("./archive/2024-2025/momento2/answers.json"),
    },
    3: {
      docenteData: () =>
        import("./archive/2024-2025/momento3/docenteData.json"),
      interaccionData: () =>
        import("./archive/2024-2025/momento3/interaccionData.json"),
      visitas: () =>
        import("./archive/2024-2025/momento3/visitas.json"),
      progresoData: () =>
        import("./archive/2024-2025/momento3/promedios.json"),
      questions: () =>
        import("./archive/2024-2025/momento3/questions.json"),
      answers: () =>
        import("./archive/2024-2025/momento3/answers.json"),
    },
  },

  "2025-2026": {
    1: {
      docenteData: () =>
        import("./current/2025-2026/momento1/docenteData.json"),
      excludedDocenteTaskIds: () =>
        import("./current/2025-2026/momento1/excludedDocenteTaskIds.json"),
      interaccionData: () =>
        import("./current/2025-2026/momento1/interaccionData.json"),
      visitas: () =>
        import("./current/2025-2026/momento1/visitas.json"),
      progresoData: () =>
        import("./current/2025-2026/momento1/promedios.json"),
      questions: () =>
        import("./current/2025-2026/momento1/questions.json"),
      answers: () =>
        import("./current/2025-2026/momento1/answers.json"),
    },
    2: {
      docenteData: () =>
        import("./current/2025-2026/momento2/docenteData.json"),
      excludedDocenteTaskIds: () =>
        import("./current/2025-2026/momento2/excludedDocenteTaskIds.json"),
      interaccionData: () =>
        import("./current/2025-2026/momento2/interaccionData.json"),
      visitas: () =>
        import("./current/2025-2026/momento2/visitas.json"),
      progresoData: () =>
        import("./current/2025-2026/momento2/promedios.json"),
      questions: () =>
        import("./current/2025-2026/momento2/questions.json"),
      answers: () =>
        import("./current/2025-2026/momento2/answers.json"),
    },
    3: {
      docenteData: () =>
        import("./current/2025-2026/momento3/docenteData.json"),
      excludedDocenteTaskIds: () =>
        import("./current/2025-2026/momento3/excludedDocenteTaskIds.json"),
      interaccionData: () =>
        import("./current/2025-2026/momento3/interaccionData.json"),
      visitas: () =>
        import("./current/2025-2026/momento3/visitas.json"),
      progresoData: () =>
        import("./current/2025-2026/momento3/promedios.json"),
      questions: () =>
        import("./current/2025-2026/momento3/questions.json"),
      answers: () =>
        import("./current/2025-2026/momento3/answers.json"),
    },
  },
};

/**
 * Public API
 */
export const loadMomentData = async (yearId, momento) => {
  const yearConfig = getYearConfig(yearId);

  if (!yearConfig) {
    console.error(`Year ${yearId} not found in dataConfig`);
    return null;
  }

  if (!isMomentoAvailable(yearId, momento)) {
    return null;
  }

  const momentLoaders = DATA_LOADERS?.[yearId]?.[momento];

  if (!momentLoaders) {
    console.warn(`No data loaders defined for ${yearId} Momento ${momento}`);
    return null;
  }

  const [
    docenteDataRaw,
    excludedDocenteTaskIds,
    interaccionData,
    visitas,
    progresoData,
    questions,
    answers,
  ] = await Promise.all([
    safeLoad(momentLoaders.docenteData),
    momentLoaders.excludedDocenteTaskIds
      ? safeLoad(momentLoaders.excludedDocenteTaskIds)
      : Promise.resolve(null),
    safeLoad(momentLoaders.interaccionData),
    safeLoad(momentLoaders.visitas),
    safeLoad(momentLoaders.progresoData),
    safeLoad(momentLoaders.questions),
    safeLoad(momentLoaders.answers),
  ]);

  // Filtro en runtime para 2025-2026: excluir docentes por task_id (red de seguridad si el JSON desplegado no estaba filtrado)
  let docenteData = docenteDataRaw;
  if (
    Array.isArray(excludedDocenteTaskIds) &&
    excludedDocenteTaskIds.length > 0 &&
    Array.isArray(docenteDataRaw)
  ) {
    const excludedSet = new Set(excludedDocenteTaskIds);
    docenteData = docenteDataRaw
      .map((colegio) => ({
        ...colegio,
        docentes: (colegio.docentes || []).filter(
          (d) => !d.task_id || !excludedSet.has(d.task_id)
        ),
      }))
      .filter((colegio) => colegio.docentes.length > 0);
  }

  return {
    docenteData,
    interaccionData,
    visitas,
    progresoData,
    autovaloracion: {
      questions,
      answers,
    },
  };
};

export const hasData = (data) => {
  if (!data) return false;

  return (
    (Array.isArray(data.docenteData) && data.docenteData.length > 0) ||
    (Array.isArray(data.visitas) && data.visitas.length > 0) ||
    (Array.isArray(data.interaccionData) &&
      data.interaccionData.length > 0) ||
    (Array.isArray(data.progresoData) &&
      data.progresoData.length > 0) ||
    (Array.isArray(data.autovaloracion?.questions) &&
      data.autovaloracion.questions.length > 0)
  );
};
